import React from 'react';
import { Modal, UnstyledButton } from '@mantine/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { IconProp } from '@fortawesome/fontawesome-svg-core';
import classes from './pick-list-modal.module.scss';

export type PickListOption = { id: number; name: string };

export type PickListModalProps = {
  title: string;
  options: ReadonlyArray<PickListOption> | null;
  disabled?: boolean;
  icon?: IconProp;
  dataTestId?: string;
  optionColor?: 'default' | 'primary' | 'success';
  onPick: (id: number) => void;
  onClose: () => void;
};

// Summary: Bare list picker — {id,name} options as light rows; shared by drawer + printer pickers.
// One-tap pick (no separate confirm step) — row style matches the select-then-confirm pickers
// (e.g. ReportPrinterPickModal) for visual consistency, but onPick fires immediately on tap.
const PickListModalComponent: React.FC<PickListModalProps> = ({ title, options, disabled = false, icon, dataTestId, optionColor = 'default', onPick, onClose }) => (
  <Modal
    opened={options !== null}
    onClose={() => {
      if (!disabled) {
        onClose();
      }
    }}
    title={<strong className={classes.title}>{title}</strong>}
    centered
    radius="md"
    zIndex={1200}
    overlayProps={{ opacity: 0.55, blur: 2 }}
    classNames={{ header: classes.header, title: classes.headerTitle }}
    closeOnClickOutside={!disabled}
    closeOnEscape={!disabled}
    closeButtonProps={{ disabled, 'data-testid': dataTestId ? `${dataTestId}-close` : undefined } as Record<string, unknown>}
  >
    <div className={classes.body}>
      <div className={classes.optionList}>
        {(options ?? []).map((option) => (
          <UnstyledButton
            key={option.id}
            component="button"
            type="button"
            className={`${classes.optionItem} ${optionColor === 'success' ? classes.optionItemSuccess : ''} ${optionColor === 'primary' ? classes.optionItemPrimary : ''}`}
            data-testid={dataTestId ? `${dataTestId}-option-${option.id}` : undefined}
            onClick={() => onPick(option.id)}
            disabled={disabled}
          >
            <span className={classes.optionContent}>
              {icon && <FontAwesomeIcon icon={icon} />}
              <span>{option.name}</span>
            </span>
          </UnstyledButton>
        ))}
      </div>
    </div>
  </Modal>
);

export const PickListModal = React.memo(PickListModalComponent);
